import {
    AfterViewInit,
    Component,
    computed,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Pipe,
    PipeTransform,
    Renderer2,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CePillComponent, CePillState, CePillStyle } from '../ce-pill/ce-pill.component';
import { CeCheckboxChange, CeCheckboxComponent } from '../ce-checkbox/ce-checkbox.component';
import { CeLinkButtonComponent } from '../ce-link-button/ce-link-button.component';
import { FormsModule } from '@angular/forms';
import { CeSpinningLoaderComponent } from '../ce-spinning-loader/ce-spinning-loader.component';
import { CeIconComponent } from '../ce-icon/ce-icon.component';
import { CeButtonComponent } from '../ce-button/ce-button.component';
import { CeAnimations } from '../ce-animation/ce-animation.lib';
import { v4 as uuid } from 'uuid';
import { CeStatusBoxComponent, StatusBoxState } from '../ce-status-box/ce-status-box.component';
import { CeTooltipDirective } from '../ce-tooltip/ce-tooltip-directive';
import { CeInfoButtonComponent } from '../ce-info-button/ce-info-button.component';
import { RouterLink } from '@angular/router';
import { CeSkeletonLoaderImports } from '../ce-skeleton-loader/ce-skeleton-loader.imports';
import { DsContextMenuImports } from '../ds-context-menu/ds-context-menu.imports';
import { DsListImports } from '../ds-list/ds-list.component';
import { CePopoverComponent, CePopoverImports } from '../ce-popover/ce-popover.component';
import { CePopoverOverlayComponent } from '../ce-popover/ce-popover-overlay.component';
import { CePopoverPosition } from '../ce-popover/ce-popover-trigger.directive';

/**
 * CeActionTable is a custom table component that allows for the display of data in a table format.
 * Use either text, or icon with a (hidden) text that will explain the icon for accessibility.
 */
export interface CeActionTableAction {
    text: string;
    icon?: string;
    action: () => void;
    inProgress?: boolean;
}

export interface CeActionTableDashedTextWithPopover {
    text: string;
    /** HTML string rendered inside the popover */
    popoverHtml?: string;
    /** ng-template reference rendered inside the popover (takes priority over popoverHtml) */
    popoverTemplate?: TemplateRef<unknown>;
    /** Popover placement relative to the trigger element */
    popoverPosition?: Extract<CePopoverPosition, 'below-left' | 'above-left'>;
}

export interface CeActionTablePill {
    text: string;
    icon?: string;
    state: CePillState;
    style?: CePillStyle;
}

export interface CeActionTableStatusBox {
    label: string;
    icon?: string;
    state: StatusBoxState;
}

export interface CeActionTableLink {
    text: string;
    href: string;
    tooltipText?: string;
}

export interface CeActionTableRouterLink {
    text: string;
    routerLink: string | ['', { outlets: { dialog: [string, string] } }];
    tooltipText?: string;
}

export interface CeActionTablePlaceholder {
    text: string;
    placeholder: boolean;
}

export interface CeActionTableTextAlignedRight {
    text: string;
    textAlignedRight: boolean;
    unit?: string;
    placeholder?: boolean;
}

export interface CeActionTableTwoRowCell {
    text: string;
    secondaryText: string;
    pill?: CeActionTablePill;
}

interface CeActionTableDsContextMenuItem {
    label: string;
    icon?: string;
    disabled?: boolean;
    action: () => void;
}

interface CeActionTableDsContextMenu {
    header?: string;
    items: CeActionTableDsContextMenuItem[];
}

export interface CeActionTableContextMenu {
    contextMenu: CeActionTableDsContextMenu;
}

export type CeActionTableContent = Record<string, Content>;

export type Content =
    | CeActionTableLink
    | CeActionTableRouterLink
    | CeActionTablePill
    | CeActionTablePill[]
    | CeActionTableStatusBox[]
    | CeActionTableAction
    | CeActionTableContextMenu
    | CeActionTablePlaceholder
    | CeActionTableTextAlignedRight
    | CeActionTableTwoRowCell
    | CeActionTableDashedTextWithPopover
    | string
    | undefined
    | null; // Define the type of the content - Add more options as needed

export interface CeActionTableRow {
    content: CeActionTableContent;
    checkboxId?: string;
    selected?: boolean;
    disabled?: boolean;
    children?: CeActionTableRow[];
    collapsed?: boolean;
    hideCheckbox?: boolean;
}

interface CeActionTableHeaderWithInfoButton {
    text: string;
    infoButton: boolean;
    component: unknown;
    hidden?: boolean;
    id?: string;
}

export interface CeActionTableHeaderWithFixedWidth {
    text: string;
    width: '6xl' | '7xl'; // Add new tokens if needed
    textAlignedRight?: boolean;
    hidden?: boolean;
    id?: string;
}

export interface CeActionTableHeaderAlignedRight {
    text: string;
    textAlignedRight: boolean;
    hidden?: boolean;
    id?: string;
}

export interface CeActionTableHeaderHideable {
    text: string;
    hidden?: boolean;
    id?: string;
}

type CeActionTableHeader =
    | string
    | CeActionTableHeaderHideable
    | CeActionTableHeaderWithInfoButton
    | CeActionTableHeaderAlignedRight
    | CeActionTableHeaderWithFixedWidth;

export interface CeActionTable {
    headers?: CeActionTableHeader[];
    rows?: CeActionTableRow[];
    isSelectable?: boolean;
    readOnly?: boolean;
}

type ScrollSourceType = 'header' | 'container' | 'scroll';

export const ceTableCellPlaceholder: CeActionTablePlaceholder = {
    text: 'Unknown',
    placeholder: true,
};

export const ceTableCellPlaceholderRight: CeActionTableTextAlignedRight = {
    text: 'Unknown',
    placeholder: true,
    textAlignedRight: true,
};

export const ceTableCellPlaceholderWaiting: CeActionTablePlaceholder = {
    text: 'Waiting for data',
    placeholder: true,
};

export const ceTableCellPlaceholderWaitingRight: CeActionTableTextAlignedRight = {
    text: 'Waiting for data',
    placeholder: true,
    textAlignedRight: true,
};

// Pipe that returns Content, depending on how CeActionTableHeader is structured.
@Pipe({
    name: 'getContentPipe',
    standalone: true,
})
export class GetContentPipePipe implements PipeTransform {
    transform(rowContent: CeActionTableContent, header: CeActionTableHeader): Content {
        if (typeof header === 'string') {
            return rowContent[header];
        } else {
            return rowContent[header.text];
        }
    }
}

@Component({
    selector: 'ce-action-table',
    imports: [
        CommonModule,
        CePillComponent,
        CeSpinningLoaderComponent,
        CeCheckboxComponent,
        CeLinkButtonComponent,
        FormsModule,
        CeIconComponent,
        CeButtonComponent,
        CeStatusBoxComponent,
        CeTooltipDirective,
        GetContentPipePipe,
        CeInfoButtonComponent,
        RouterLink,
        CeSkeletonLoaderImports,
        DsContextMenuImports,
        DsListImports,
        CePopoverComponent,
        CePopoverOverlayComponent,
        CePopoverImports
    ],
    animations: [CeAnimations],
    templateUrl: './ce-action-table.component.html',
    styleUrl: './ce-action-table.component.scss',
    encapsulation: ViewEncapsulation.None,
    host: {
        '[class]': '_computedClass()',
    }
})
export class CeActionTableComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    @Input({ required: true }) tableData!: CeActionTable | undefined;
    @Output() selectedItems: EventEmitter<CeActionTableRow[]> = new EventEmitter<CeActionTableRow[]>(); // Create a new EventEmitter
    @Output() loadMoreActivated: EventEmitter<void> = new EventEmitter<void>();
    firstColumnSticky = input<boolean>(false);
    lastColumnSticky = input<boolean>(false);
    lastRowLoading = input<boolean>(false);
    hasLoadMoreButton = input<boolean>(false);
    allResultsHaveLoaded = input<boolean>(false);

    readonly #renderer = inject(Renderer2);
    readonly #tableElement = inject(ElementRef).nativeElement;
    readonly #ngZone = inject(NgZone);

    @ViewChild('stickyHeader') stickyHeader!: ElementRef<HTMLDivElement>;
    @ViewChild('container') container!: ElementRef<HTMLDivElement>;
    @ViewChild('stickyScroll') scroll!: ElementRef<HTMLDivElement>;

    tableContainsSecondLevel: boolean | undefined = false;
    topLevelSelected = false;
    topLevelMixed = false;
    topLevelCheckboxId: string = uuid();
    checkboxes: Map<string, boolean> = new Map();
    #tableObservers: (IntersectionObserver | ResizeObserver)[] = [];
    #ignoreScroll: { [key in ScrollSourceType]: boolean } = {
        header: false,
        container: false,
        scroll: false,
    };
    #scrollEventHandlers: { [key in ScrollSourceType]: EventListener } = {} as {
        [key in ScrollSourceType]: EventListener;
    };

    hasItems = computed((): boolean => {
        return this.tableData?.rows?.length !== 0;
    });

    showResultsHaveLoaded = computed((): boolean => {
        return !this.lastRowLoading() && this.allResultsHaveLoaded();
    });

    showLoadMoreButton = computed((): boolean => {
        return !this.lastRowLoading() && this.hasItems() && this.hasLoadMoreButton();
    });

    protected readonly _computedClass = computed(() => {
        const classes = ['CeActionTable'];
        if (this.firstColumnSticky()) {
            classes.push(`CeActionTable--sticky-first-column`);
        }
        if (this.lastColumnSticky()) {
            classes.push(`CeActionTable--sticky-last-column`);
        }
        return classes.join(' ');
    });

    readonly #toggleLastColumnStickiness: ConstructorParameters<typeof IntersectionObserver>[0] = (
        entries: IntersectionObserverEntry[]
    ): void => {
        for (const entry of entries) {
            const table: HTMLElement = entry.target.closest('table') as HTMLElement;

            // Toggle class that handles border styles, when second to last column is fully hidden/visible
            if (entry.intersectionRatio === 1) {
                table?.classList.remove('sticky-last-column-on');
            } else {
                table?.classList.add('sticky-last-column-on');
            }
        }
    };

    readonly #toggleFirstColumnStickiness: ConstructorParameters<typeof IntersectionObserver>[0] = (
        entries: IntersectionObserverEntry[]
    ): void => {
        for (const entry of entries) {
            const table: HTMLElement = entry.target.closest('table') as HTMLElement;

            // Toggle class that handles border styles, when second column is fully hidden/visible
            if (entry.intersectionRatio === 1) {
                table?.classList.remove('sticky-first-column-on');
            } else {
                table?.classList.add('sticky-first-column-on');
            }
        }
    };

    readonly #toggleFirstColumnShadow: ConstructorParameters<typeof IntersectionObserver>[0] = (
        entries: IntersectionObserverEntry[]
    ): void => {
        for (const entry of entries) {
            const table: HTMLElement = entry.target.closest('ce-action-table') as HTMLElement;

            if (entry.intersectionRatio === 1) {
                table?.classList.remove('first-column-shadow-on');
            } else {
                table?.classList.add('first-column-shadow-on');
            }
        }
    };

    readonly #toggleLastColumnShadow: ConstructorParameters<typeof IntersectionObserver>[0] = (
        entries: IntersectionObserverEntry[]
    ): void => {
        for (const entry of entries) {
            const table: HTMLElement = entry.target.closest('ce-action-table') as HTMLElement;

            if (entry.intersectionRatio === 1) {
                table?.classList.remove('last-column-shadow-on');
            } else {
                table?.classList.add('last-column-shadow-on');
            }
        }
    };

    onLoadMoreClick(): void {
        this.loadMoreActivated.emit();
    }

    getType(item: Content): string {
        if (typeof item !== 'object' || item === null) {
            return 'string';
        }

        if (Object.keys(item).length === 0) {
            return 'undefined';
        }

        if ('href' in item && 'text' in item) {
            return 'External link';
        }

        if ('routerLink' in item && 'text' in item) {
            return 'Router link';
        }

        if ('contextMenu' in item) {
            return 'Context menu';
        }

        if ('popoverHtml' in item || 'popoverTemplate' in item) {
            return 'Dashed text with popover';
        }

        if ('text' in item && 'secondaryText' in item) {
            return 'Two row cell';
        }

        if (Array.isArray(item)) {
            if ('type' in item[0] && item[0].type === 'statusBox') {
                return 'StatusBox';
            }
            if ('state' in item[0] && 'text' in item[0]) {
                return 'PillArray';
            }
        }

        if ('textAlignedRight' in item && item.textAlignedRight) {
            return 'Text aligned right';
        }

        const hasStateAndText: boolean = 'state' in item && 'text' in item;
        const isPlaceholder: boolean = 'placeholder' in item;
        const hasActionAndText: boolean = 'action' in item && 'text' in item;
        const hasActionAndIcon: boolean = 'action' in item && 'icon' in item;

        if (hasStateAndText) {
            return 'Pill';
        }

        if (hasActionAndText) {
            return 'Action';
        }

        if (hasActionAndIcon) {
            return 'Action';
        }

        if (isPlaceholder) {
            return 'Placeholder';
        }

        return 'string';
    }

    #updateCheckbox(checked: boolean | 'mixed', tableRow: CeActionTableRow, parent?: boolean): void {
        if (checked === 'mixed') {
            tableRow.selected = false;
            if (!tableRow.hideCheckbox) {
                this.checkboxes.set(tableRow.checkboxId as string, false);
            }
        } else {
            tableRow.selected = checked;
            if (!tableRow.hideCheckbox) {
                this.checkboxes.set(tableRow.checkboxId as string, checked);
            }
        }
        if (parent && tableRow.checkboxId) {
            document.getElementById(tableRow.checkboxId)?.setAttribute('aria-checked', checked.toString());
        }
    }

    #updateTopLevelCheckbox(): void {
        const selectedCheckboxes: Array<boolean> = [];

        this.checkboxes.forEach((checkboxChecked: boolean): void => {
            selectedCheckboxes.push(checkboxChecked);
        });

        if (selectedCheckboxes.includes(true) && selectedCheckboxes.includes(false)) {
            this.topLevelSelected = false;
            this.topLevelMixed = true;
        } else {
            this.topLevelSelected = !selectedCheckboxes.includes(false);
            this.topLevelMixed = false;
        }
    }

    onItemSelect(value: CeCheckboxChange, item: CeActionTableRow, parent?: CeActionTableRow): void {
        if (!this.selectedItems || this.tableData?.readOnly) {
            return;
        }
        // Update the status of checkbox clicked (item)
        this.#updateCheckbox(value.checked, item, !parent);

        // Emit the selected items and update checkbox statuses
        const selectedItems: CeActionTableRow[] = [];
        (this.tableData as CeActionTable)?.rows?.forEach((row: CeActionTableRow): void => {
            row.children?.forEach((child: CeActionTableRow): void => {
                // Update the statuses of item's children checkboxes
                if (row === item) {
                    this.#updateCheckbox(value.checked, child);
                }

                // Update the status of parent checkbox
                if (row === parent) {
                    const checkboxesCheckedArray: Array<boolean> = [];

                    parent?.children?.forEach((child: CeActionTableRow): void => {
                        checkboxesCheckedArray.push(child.selected ? child.selected : false);
                    });

                    if (!checkboxesCheckedArray.includes(true)) {
                        this.#updateCheckbox(false, parent, true);
                    } else if (!checkboxesCheckedArray.includes(false)) {
                        this.#updateCheckbox(true, parent, true);
                    } else {
                        this.#updateCheckbox('mixed', parent, true);
                    }
                }

                if (child.selected) {
                    selectedItems.push(child);
                }
            });

            if (row.selected) {
                selectedItems.push(row);
            }
        });
        this.#updateTopLevelCheckbox();
        this.selectedItems.emit(selectedItems);
    }

    onTopLevelSelect(value: CeCheckboxChange): void {
        if (!this.selectedItems || this.tableData?.readOnly) {
            return;
        }

        // Emit the selected items and update checkbox statuses
        const selectedItems: CeActionTableRow[] = [];
        (this.tableData as CeActionTable)?.rows?.forEach((row: CeActionTableRow): void => {
            // Update the statuses of all parent checkboxes
            this.#updateCheckbox(value.checked, row, true);
            if (row.selected) {
                selectedItems.push(row);
            }

            // Update the statuses of all child checkboxes
            row.children?.forEach((child: CeActionTableRow): void => {
                this.#updateCheckbox(value.checked, child);

                if (child.selected) {
                    selectedItems.push(child);
                }
            });
        });
        this.#updateTopLevelCheckbox();
        this.selectedItems.emit(selectedItems);
    }

    initializeObservers(): void {
        const tableContainer = this.#tableElement.querySelector('.CeActionTable-container');

        if (this.firstColumnSticky()) {
            this.initStickyFirstColumnObserver();
        }

        if (this.lastColumnSticky()) {
            this.initStickyLastColumnObserver();
        }

        if (tableContainer?.scrollWidth > tableContainer?.offsetWidth) {
            if (!this.lastColumnSticky()) {
                this.initLastColumnScrollObserver();
            }
            if (!this.firstColumnSticky()) {
                this.initFirstColumnScrollObserver();
            }
        }
    }

    readonly adjustUiOnResize = (): void => {
        const containerEl = this.container?.nativeElement;
        const scrollEl = this.scroll?.nativeElement;
        const headerEl = this.stickyHeader?.nativeElement;
        const tableEl = this.#tableElement?.querySelector('table');

        if (!containerEl || !scrollEl || !headerEl || !tableEl) return;

        const spacer = scrollEl.querySelector('.CeActionTable-scroll');
        const tableFirstRow = tableEl.querySelector('tr:first-of-type');
        const stickyThs = headerEl.querySelectorAll('th');
        const hiddenThs = containerEl.querySelectorAll('th');

        if (!spacer || !tableFirstRow || !stickyThs.length) return;

        const scrollWidth = containerEl.scrollWidth;
        const firstRowHeight = hiddenThs[0].clientHeight;
        const negativeHeight = `${-firstRowHeight}px`;
        const colWidths = Array.from(hiddenThs).map((th) => th.clientWidth);

        // Spacer width
        this.#renderer.setStyle(spacer, 'width', `${scrollWidth}px`);

        // Table adjustments: hide original th
        this.#renderer.setStyle(tableEl, 'margin-top', negativeHeight);

        // Header height minus border width (1px)
        this.#renderer.setStyle(headerEl, 'height', `${firstRowHeight - 1}px`);

        // When table is inside 'TwoColLayout', the header should stick right below page header
        if (headerEl.closest('.TwoColLayout-right')) {
            headerEl.classList.add('stick-to-page-header');
        }

        // Column widths
        stickyThs.forEach((th, i) => {
            const width = colWidths[i];
            if (width != null) {
                this.#renderer.setStyle(th, 'width', `${width}px`);
            }
        });
    };

    readonly #syncScroll = (source: ScrollSourceType): void => {
        const headerEl = this.stickyHeader?.nativeElement;
        const containerEl = this.container?.nativeElement;
        const scrollEl = this.scroll?.nativeElement;

        const scrollableElements = { header: headerEl, container: containerEl, scroll: scrollEl };
        const newLeftScrollPos = scrollableElements[source].scrollLeft;

        // Mark non-source elements to ignore next scroll, so there's no multiple event listeners firing
        for (const key of Object.keys(scrollableElements) as ScrollSourceType[]) {
            this.#ignoreScroll[key] = key !== source;
        }

        // Sync 'scrollLeft' of all scrollable elements
        for (const el of Object.values(scrollableElements)) {
            el.scrollLeft = newLeftScrollPos;
        }
    };

    /**
     *  Observe the width of table's real (hidden) THs. Watches out for:
     *    - browser width changes
     *    - table content changes
     */
    observeHeaderWidth(): void {
        const resizeObserver = new ResizeObserver(this.adjustUiOnResize);
        resizeObserver.observe(this.#tableElement.querySelector('table .CeActionTable-header'));
        this.#tableObservers.push(resizeObserver);

        // If table is nested, add another observer to second column (usually "Name"), to see if nested rows are expanded/collapsed
        if (this.#tableElement.querySelector('.CeActionTable-row-item--empty button[aria-expanded]')) {
            const nestedTableObserver = new ResizeObserver(this.adjustUiOnResize);
            nestedTableObserver.observe(
                this.#tableElement.querySelector('table .CeActionTable-header th:nth-of-type(2)')
            );
            this.#tableObservers.push(nestedTableObserver);
        }
    }

    initializeStickyHeaderAndScroll(): void {
        if (!this.container?.nativeElement || !this.stickyHeader?.nativeElement || !this.scroll?.nativeElement) return;

        const scrollableElements: { [key in ScrollSourceType]: HTMLElement } = {
            header: this.stickyHeader?.nativeElement,
            container: this.container?.nativeElement,
            scroll: this.scroll?.nativeElement,
        };

        // Add scroll listeners
        for (const key of Object.keys(scrollableElements) as ScrollSourceType[]) {
            const el = scrollableElements[key];
            const scrollHandler = () => {
                this.#ngZone.runOutsideAngular(() => {
                    if (this.#ignoreScroll[key]) {
                        this.#ignoreScroll[key] = false;
                        return;
                    }
                    this.#syncScroll(key);
                });
            };

            // Store the callback function to remove it later
            this.#scrollEventHandlers[key] = scrollHandler;

            el.addEventListener('scroll', scrollHandler, { passive: true });
        }
    }

    ngOnInit(): void {
        const itemsWithChildren: boolean[] | undefined = [];
        const newTableData: CeActionTableRow[] = [];

        this.tableData?.rows?.forEach((row: CeActionTableRow): void => {
            const id: string = uuid();

            // Push all parent level checkboxes to a Map
            this.checkboxes.set(id, row.selected ?? false);

            if (row.children) {
                const newChildrenData: CeActionTableRow[] = [];

                row.children.forEach((child: CeActionTableRow, index: number): void => {
                    // Update the statuses of children checkboxes
                    if (row.selected) {
                        child.selected = true;
                    }

                    // Enrich original data with field 'checkboxId'
                    newChildrenData.push({ ...child, checkboxId: id + '-' + index });

                    // Push all child level checkboxes to a Map (skip hidden checkboxes)
                    if (!child.hideCheckbox) {
                        this.checkboxes.set(id + '-' + index, child.selected ?? false);
                    }
                });

                // Replace original data 'children' with enriched version
                row.children = newChildrenData;
            }

            newTableData.push({ ...row, checkboxId: id });
            itemsWithChildren.push(!!row.children);
        });
        this.tableContainsSecondLevel = itemsWithChildren?.includes(true);

        if (newTableData.length > 0 && this.tableData) {
            // Replace original data 'rows' with enriched version
            this.tableData.rows = newTableData;
        }
        this.#updateTopLevelCheckbox();
    }

    ngAfterViewInit(): void {
        this.initializeObservers();
        this.observeHeaderWidth();
        this.initializeStickyHeaderAndScroll();
    }

    ngOnChanges({
        tableData,
    }: {
        tableData?: {
            currentValue: { headers: CeActionTableHeader[] };
            previousValue: { headers: CeActionTableHeader[] };
        };
    }): void {
        const prevHeaders = tableData?.previousValue?.headers;
        const currentHeaders = tableData?.currentValue?.headers;

        // If table columns' length changes, calculate the need for scroll shadows again
        if (prevHeaders && prevHeaders?.length !== currentHeaders?.length) {
            // Give HTML (columns) time to render before calculations
            setTimeout(() => {
                this.initializeObservers();
            });
        }

        // If table columns' order changes, calculate new width for sticky table's headers
        if (prevHeaders && JSON.stringify(prevHeaders) !== JSON.stringify(currentHeaders)) {
            // Give HTML (columns) time to render before calculations
            setTimeout(() => {
                this.adjustUiOnResize();
            });
        }
    }

    ngOnDestroy(): void {
        if (this.#tableObservers.length === 0) {
            return;
        }

        // Disconnect the observer when the component is destroyed to avoid memory leaks
        for (const observer of this.#tableObservers) {
            observer.disconnect();
        }
        this.#tableObservers = [];

        const scrollableElements: { [key in ScrollSourceType]: HTMLElement } = {
            header: this.stickyHeader?.nativeElement,
            container: this.container?.nativeElement,
            scroll: this.scroll?.nativeElement,
        };

        for (const key of Object.keys(scrollableElements) as ScrollSourceType[]) {
            const el = scrollableElements[key];
            const callback = this.#scrollEventHandlers[key];

            // Ensure the element and callback exist before trying to remove the listener
            if (el && callback) {
                el.removeEventListener('scroll', callback);
            }
        }
    }

    initStickyFirstColumnObserver(): void {
        // Add observer to the second column's heading
        let observedCell = this.#tableElement.querySelector('table td:nth-of-type(2)') as HTMLElement;
        let previousCell = this.#tableElement.querySelector('table td:first-of-type') as HTMLElement;
        let previousCellWidth = previousCell ? previousCell.clientWidth - 1 : 0;

        // If table is nested, add observer to the third column's heading
        if (previousCell.classList.contains('CeActionTable-row-item--empty')) {
            const chevronCell = this.#tableElement.querySelector('table td:first-of-type') as HTMLElement;
            previousCell = this.#tableElement.querySelector('table td:nth-of-type(2)') as HTMLElement;
            observedCell = this.#tableElement.querySelector('table td:nth-of-type(3)') as HTMLElement;
            previousCellWidth = previousCell ? previousCell.clientWidth + chevronCell?.clientWidth - 1 : 0;
        }

        if (observedCell) {
            const firstColumnObserver = new IntersectionObserver(this.#toggleFirstColumnStickiness, {
                threshold: [0, 1],
                root: this.#tableElement,
                rootMargin: `0px 0px 0px -${previousCellWidth}px`,
            });
            firstColumnObserver.observe(observedCell);
            this.#tableObservers.push(firstColumnObserver);
        }
    }

    initStickyLastColumnObserver(): void {
        // Add observer to the second to last column's heading
        const observedCell = this.#tableElement.querySelector('table td:nth-last-of-type(2)') as HTMLElement;
        const nextCell = this.#tableElement.querySelector('table td:last-of-type') as HTMLElement;
        const nextCellWidth = nextCell ? nextCell.clientWidth - 1 : 0;

        if (observedCell) {
            const lastColumnObserver = new IntersectionObserver(this.#toggleLastColumnStickiness, {
                threshold: [0, 1],
                root: this.#tableElement,
                rootMargin: `0px -${nextCellWidth}px 0px 0px`,
            });
            lastColumnObserver.observe(observedCell);
            this.#tableObservers.push(lastColumnObserver);
        }
    }

    initFirstColumnScrollObserver(): void {
        const observedCell = this.#tableElement.querySelector('table td:nth-of-type(2)') as HTMLElement;

        if (observedCell) {
            const firstColumnScroll = new IntersectionObserver(this.#toggleFirstColumnShadow, {
                threshold: [0, 1],
                root: this.#tableElement,
            });
            firstColumnScroll.observe(observedCell);
            this.#tableObservers.push(firstColumnScroll);
        }
    }

    initLastColumnScrollObserver(): void {
        const observedCell = this.#tableElement.querySelector('table td:nth-last-of-type(2)') as HTMLElement;

        if (observedCell) {
            const lastColumnScroll = new IntersectionObserver(this.#toggleLastColumnShadow, {
                threshold: [0, 1],
                root: this.#tableElement,
            });
            lastColumnScroll.observe(observedCell);
            this.#tableObservers.push(lastColumnScroll);
        }
    }
}
